<template>
  <div class="brandBoxWrap">
    <el-popover ref="popover"
                placement="bottom-start" content=""
                title="" width="" trigger="manual"
                popper-class="brandBoxPopper"
                v-model="popoverVisible">
      <div class="brandBox" :class="{brandBox1:!isShow}" slot="reference" ref="reference" @click="popoverVisible = !popoverVisible">
        <AvatarInitial :name="selectedDisplayName" size="lg"/>
        <template v-if="isShow">
          <AppTooltip v-if="isNameTruncated" :content="selectedDisplayName" placement="bottom">
            <div class="name" ref="nameEl">{{selectedDisplayName}}</div>
          </AppTooltip>
          <div v-else class="name" ref="nameEl">{{selectedDisplayName}}</div>
        </template>
        <Icon name="unfold-more" class="more"/>
      </div>
      <div class="brandList">
        <p v-for="(item,index) in brandList" :class="{active:item.name === selectedBrandName}" :key="index" @click="changeBrandData(item)">{{displayName(item.name)}}</p>
      </div>
    </el-popover>
  </div>
</template>

<script>
// 账号级的「主品牌」切换器 —— 列表来自 /auth/entities 的 clusters（存在 auth.brands，见 utils/cluster.js），和 hotelBox.vue
// 切换酒店是同一个概念层级。注意它跟侧边栏 "Brand Overview" 下面那个品牌列表
// （RouterMenu.vue / store/modules/brandNav.js）是两回事：那边切的是"当前在看组合里
// 的哪个品牌"，纯前端下钻，不会也不该反过来改这里的 selectedBrandName。
import { Icon } from 'tdesign-icons-vue'
import { mapState } from "vuex";
import AvatarInitial from "@/components/common/AvatarInitial.vue";
import AppTooltip from "@/components/common/AppTooltip.vue";
import { firstHotelOfCluster, clusterDisplayName } from "@/utils/cluster";

export default {
  name: "BrandBox",
  props: {
    isShow: {
      default: false,
    }
  },
  components: {
    Icon,
    AvatarInitial,
    AppTooltip,
  },
  data() {
    return {
      popoverVisible: false,
      // Only wrap the name in a tooltip once it's actually clamped — showing
      // a hover popup that just repeats text already fully visible is noise.
      isNameTruncated: false,
    };
  },
  computed: {
    ...mapState({
      selectedBrandName: state => state.auth.selectedBrandName,
      brandList: state => state.auth.brands,
      hotelList: state => state.hotel.hotelList,
    }),
    // 显示用名字：cluster 名里的下划线换成空格（Marriott_China → Marriott China）
    selectedDisplayName() {
      return clusterDisplayName(this.selectedBrandName);
    },
  },
  watch: {
    selectedBrandName() {
      this.$nextTick(this.checkNameTruncated);
    },
    isShow() {
      this.$nextTick(this.checkNameTruncated);
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    this.$nextTick(this.checkNameTruncated);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    displayName: clusterDisplayName,
    // -webkit-line-clamp hides overflow without reflowing layout, so
    // scrollHeight (the untruncated content height) exceeding clientHeight
    // (the clamped box's rendered height) reliably means text was cut off.
    checkNameTruncated() {
      const el = this.$refs.nameEl;
      this.isNameTruncated = !!el && el.scrollHeight > el.clientHeight;
    },
    handleClickOutside(event) {
      if (!this.popoverVisible) return;
      const triggerBtn = this.$refs.reference;
      const popover = this.$refs.popover.$el;
      const isClickInside = triggerBtn.contains(event.target) || popover.contains(event.target);
      if (!isClickInside) this.popoverVisible = false;
    },
    changeBrandData(item) {
      this.popoverVisible = false;
      if (!item || item.name === this.selectedBrandName) return;
      this.$store.commit('auth/SET_SELECTED_BRAND', item.name);
      // 同 RouterMenu.switchModule('brand') / HotelSelector.vue 的品牌分支：顺带选中这个 cluster
      // 里的一家酒店（没有就退回同集团的一家）。集团报告按 cluster 取，选不到酒店也能看。
      const matchedHotel = firstHotelOfCluster(item, this.hotelList);
      if (matchedHotel) this.$store.commit('hotel/SET_CURRENT_HOTEL', matchedHotel.hotelId);
      localStorage.removeItem('ratingInfos');
      // 整页刷新——换主品牌等于换掉所有接口的 brandName 口径，和 hotelBox 换酒店一样
      // 用刷新把全局状态重置干净（这跟"仅在品牌模式下避免刷新"说的菜单内下钻不是一回事）。
      // 同 hotelBox：跳转前回到顶部，避免同 URL 跳转时浏览器恢复原滚动位置
      window.scrollTo(0, 0);
      window.location.href = '/BrandOverview2';
    },
  },
}
</script>

<style scoped lang="scss">
.brandBox{
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 0 9px;
  cursor: pointer;
  min-height: 48px;
  padding: 6px 4px;
  box-sizing: border-box;
  border: none;
  border-radius: 8px;
  background: transparent;
  transition: background 0.15s ease;
  &:hover {
    background: var(--sidebar-hover);
  }
  .name{
    font-family: InterMedium, sans-serif !important;
    font-weight: 600;
    font-size: 13px;
    line-height: 20px;
    white-space: wrap;
    color: var(--sidebar-text);
    flex: 1;
    min-width: 0;
    word-break: normal;
    overflow-wrap: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
  .more{
    flex-shrink: 0;
    font-size: 18px;
    color: var(--sidebar-text-muted);
  }
}
.brandBox1{
  padding: 6px;
  gap: 0;
  border: none;
  border-radius: 8px;
}
</style>

<style lang="scss">
.brandBoxPopper{
  padding: 8px;
  min-width: 300px;
  border-radius: 12px !important;
  .popper__arrow{
    display: none!important;
  }
  .brandList{
    max-height: 350px;
    overflow: auto;
    p{
      margin: 0;
      height: 40px;
      line-height: 40px;
      font-family: Inter, sans-serif !important;
      font-weight: 400;
      font-size: 14px;
      padding: 0 12px;
      cursor: pointer;
      box-sizing: border-box;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    p:hover{
      background: #F0F4F8;
      border-radius: 8px;
    }
    .active{
      background: #E6EEF6;
      border-radius: 8px;
      color: #1A365D;
      font-weight: 600;
    }
  }
}
</style>
